/**
 * Loading / error / empty blocks — TASK 8.1 Steps 1-3.
 *
 * Extracted from the mock's ONE universal-state section (L104-136, view model
 * `us` at L979-983), which every list/detail route reused. The mock rendered
 * it INSTEAD of the page; here each block is a detached element a view drops
 * into its own body, so an artist page keeps its heading and back-link while
 * its album grid is still loading — the §10.6 rule that "a single failed
 * section never forces a whole-page error".
 *
 * Copy is the contract's, not invented:
 *   error  — "SIGNAL DROPPED" + a Retry button (mock L115-119, ui-contract
 *            §10.6 "Retriable error").
 *   empty  — "NOTHING ON THIS WALL YET" + the per-surface message; on
 *            `/browse/artists` an ADMIN additionally gets the in-flow sync
 *            bridge "Check sync status →" (mock L125, §10.6 "`us.showSync`").
 *   loading— the mock's sheening skeleton list, announced once by its
 *            `role="status"` container (primitives/Skeleton.js).
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { linkTestId } from '../app/testids.js';

import { append, el } from './primitives/dom.js';
import { SkeletonList } from './primitives/Skeleton.js';

/** §10.6 — the empty-library copy shared by home and /browse/artists. */
export const LIBRARY_PREPARING_MESSAGE = 'The library is being prepared — nothing indexed yet.';
/** mock L117 — the retriable-failure message. */
export const RETRIABLE_ERROR_MESSAGE =
  'Something glitched between here and the library. Give it another shot.';

/**
 * Sheening placeholder list (mock L108-112).
 *
 * @param {{ count?: number, label?: string, variant?: 'list'|'cover'|'row' }} [options]
 * @returns {HTMLElement}
 */
export function LoadingBlock(options = {}) {
  const { count = 5, label = 'Loading', variant = 'list' } = options;
  return SkeletonList({ count, label, variant });
}

/**
 * Retriable-error card (mock L114-120). `role="status"` rather than `alert`:
 * ui-contract §10.6 records the mock's own choice for these panels, and a
 * failed section is not an interruption.
 *
 * A node INSERTED already containing text is not reliably announced by screen
 * readers even with `role="status"` — the region must exist before its text
 * changes. The player path solves this by also writing into the shell's
 * persistent polite live region (`LiveRegions.js#announce`); callers pass that
 * same function as `announce` so a failed section is actually heard.
 *
 * @param {{ message?: string, onRetry: () => void, announce?: (text: string) => void }} options
 * @returns {HTMLElement}
 */
export function ErrorBlock(options) {
  const { message = RETRIABLE_ERROR_MESSAGE, onRetry, announce } = options;
  if (announce !== undefined) announce(message);

  const card = el('div', { className: 'sm-card-error', attrs: { role: 'status' } });
  append(
    card,
    el('div', { className: 'sm-glyph sm-glyph--card', text: 'SIGNAL DROPPED' }),
    el('p', { className: 'sm-body sm-body--card', text: message }),
    el('button', {
      className: 'sm-btn sm-btn--retry',
      text: 'Retry',
      testId: TESTIDS.RETRY_BUTTON,
      attrs: { type: 'button' },
      on: { click: () => onRetry() },
    }),
  );
  return card;
}

/**
 * Empty-state panel (mock L121-127).
 *
 * @param {{
 *   headline?: string,
 *   message: string,
 *   showSyncLink?: boolean,
 *   announce?: (text: string) => void,
 * }} options
 *   `showSyncLink` is the §10.6 admin bridge; a MEMBER must never see it (the
 *   contract verifies it absent at `#/?empty=1&role=member`). `announce` is
 *   the shell's persistent polite live region — freshly inserted text is not
 *   reliably announced on its own (see {@link ErrorBlock}).
 * @returns {HTMLElement}
 */
export function EmptyBlock(options) {
  const { headline = 'NOTHING ON THIS WALL YET', message, showSyncLink = false, announce } = options;
  if (announce !== undefined) announce(message);

  const panel = el('div', { className: 'sm-empty sm-empty--16' });
  append(
    panel,
    el('div', { className: 'sm-glyph sm-glyph--empty', text: headline }),
    el('p', { className: 'sm-body sm-body--empty', text: message }),
    showSyncLink
      ? append(
          el('p', { className: 'sm-body--link-row' }),
          el('a', {
            text: 'Check sync status →',
            // NOT `sync-status-link`: ui-contract §3 contracts that id for the
            // /admin/users · /admin/sync nav links ("Sync status"). This
            // bridge has a DIFFERENT accessible name, so the contract's own
            // derivation rule ("kebab-case of the accessible name", app/
            // testids.js) gives it its own id — a Tier-1 selector for the
            // admin nav can never resolve to this in-flow bridge.
            testId: linkTestId('Check sync status'),
            attrs: { href: '#/admin/sync' },
          }),
        )
      : null,
  );
  return panel;
}

/**
 * The mock's 404 block (L129-135) — a NON-retriable miss. No Retry button:
 * §10.6 is explicit that 404 offers "path back, no Retry".
 *
 * @param {{ message: string }} options
 * @returns {HTMLElement}
 */
export function NotFoundBlock(options) {
  const block = el('div', { className: 'sm-centered-block' });
  append(
    block,
    el('div', { className: 'sm-glyph sm-glyph--404', text: '404' }),
    el('p', { className: 'sm-body sm-body--404', text: options.message }),
    el('a', {
      className: 'sm-btn sm-btn--accent',
      text: 'Take me home',
      attrs: { href: '#/' },
    }),
  );
  return block;
}

/**
 * 410 GONE — permanently removed, so like {@link NotFoundBlock} it is
 * NON-retriable: §10.6 pins 410 as "⊘ marker, retry never succeeds", and a
 * Retry button here would be a promise the server has already refused to
 * keep. Distinct from 404 because the copy is different — the thing DID
 * exist — and it carries the ⊘ glyph rather than a status code.
 *
 * @param {{ message: string }} options
 * @returns {HTMLElement}
 */
export function GoneBlock(options) {
  const block = el('div', { className: 'sm-centered-block' });
  append(
    block,
    el('div', { className: 'sm-glyph sm-glyph--404', text: '⊘' }),
    el('p', { className: 'sm-body sm-body--404', text: options.message }),
    el('a', {
      className: 'sm-btn sm-btn--accent',
      text: 'Take me home',
      attrs: { href: '#/' },
    }),
  );
  return block;
}
